/**
 * Async expression evaluator backed by a Web Worker.
 *
 * The Worker provides security isolation — user expressions execute in a
 * separate global with no DOM, storage, or network access. This module
 * manages the Worker lifecycle and provides a simple Promise-based API.
 */

import type { EvaluationContext, EvaluationResult } from './types';
import type { WorkerRequest, WorkerResponse } from './expressionWorker';

let worker: Worker | null = null;
let nextId = 0;
const pending = new Map<number, {
  resolve: (r: EvaluationResult | EvaluationResult[]) => void;
  // Resolves the in-flight promise with a shape-correct failure (single object vs array)
  // so terminate/worker-error never leaves a caller hanging.
  fail: (error: string) => void;
  timer: ReturnType<typeof setTimeout>;
}>();

const TIMEOUT_MS = 5000;

/**
 * Validate a message received from the worker before trusting it.
 *
 * The worker executes untrusted user expressions, and user code can reach the
 * worker-global `postMessage` to send arbitrary payloads back to this thread.
 * We only act on messages that match our known response contract ({ id, type,
 * result|results }); anything malformed is ignored so a forged message can't
 * corrupt pending-request bookkeeping or crash the handler.
 */
function isWorkerResponse(data: unknown): data is WorkerResponse {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  if (typeof d.id !== 'number') return false;
  if (d.type === 'result') return typeof d.result === 'object' && d.result !== null;
  if (d.type === 'batchResult') return Array.isArray(d.results);
  return false;
}

function getWorker(): Worker {
  if (!worker) {
    try {
      worker = new Worker(
        new URL('./expressionWorker.ts', import.meta.url),
        { type: 'module' },
      );
    } catch {
      // Worker not available (e.g., jsdom tests, SSR) — throw so callers handle gracefully
      throw new Error('Web Worker not available in this environment');
    }
    worker.onmessage = (e: MessageEvent) => {
      if (!isWorkerResponse(e.data)) return; // ignore malformed/forged messages
      const data = e.data;
      const entry = pending.get(data.id);
      if (!entry) return;
      clearTimeout(entry.timer);
      pending.delete(data.id);
      if (data.type === 'batchResult') {
        entry.resolve(data.results);
      } else {
        entry.resolve(data.result);
      }
    };
    worker.onerror = (err) => {
      for (const [id, entry] of pending) {
        clearTimeout(entry.timer);
        entry.fail(`Worker error: ${err.message}`);
        pending.delete(id);
      }
      worker?.terminate();
      worker = null;
    };
  }
  return worker;
}

function sendRequest<T>(msg: WorkerRequest, timeout: number): Promise<T> {
  // Echo the caller's input expression on every failure path so downstream UI/debugging
  // keeps the context (matches the worker's normal `{ expression }` echo).
  const expression = 'expression' in msg ? msg.expression : '';
  return new Promise((resolve) => {
    let w: Worker;
    try {
      w = getWorker();
    } catch {
      resolve({ success: false, error: 'Worker unavailable', expression } as T);
      return;
    }

    const timer = setTimeout(() => {
      pending.delete(msg.id);
      resolve({ success: false, error: 'Evaluation timed out', expression } as T);
      worker?.terminate();
      worker = null;
    }, timeout);

    pending.set(msg.id, {
      resolve: resolve as (r: EvaluationResult | EvaluationResult[]) => void,
      fail: (error: string) => resolve({ success: false, error, expression } as T),
      timer,
    });

    // postMessage can throw (e.g. DataCloneError on a non-cloneable context) — clean up the
    // pending entry/timer and resolve a shape-correct failure instead of rejecting.
    try {
      w.postMessage(msg);
    } catch (err) {
      clearTimeout(timer);
      pending.delete(msg.id);
      resolve({
        success: false,
        error: `Failed to start evaluation: ${err instanceof Error ? err.message : String(err)}`,
        expression,
      } as T);
    }
  });
}

/**
 * Evaluate a single expression in the isolated worker.
 */
export function evaluateAsync(
  expression: string,
  context: EvaluationContext,
  timeout = TIMEOUT_MS,
): Promise<EvaluationResult> {
  const id = ++nextId;
  return sendRequest<EvaluationResult>(
    { id, type: 'evaluate', expression, context },
    timeout,
  );
}

/**
 * Evaluate multiple expressions in a single worker round-trip.
 * Returns results in the same order as the input expressions.
 */
export function evaluateBatchAsync(
  expressions: string[],
  context: EvaluationContext,
  timeout = TIMEOUT_MS,
): Promise<EvaluationResult[]> {
  if (expressions.length === 0) return Promise.resolve([]);

  let w: Worker;
  try {
    w = getWorker();
  } catch {
    // Worker unavailable — return failure for each expression
    return Promise.resolve(
      expressions.map((expr) => ({ success: false as const, error: 'Worker unavailable', expression: expr }))
    );
  }

  const id = ++nextId;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      resolve(expressions.map((expr) => ({ success: false as const, error: 'Evaluation timed out', expression: expr })));
      worker?.terminate();
      worker = null;
    }, timeout);

    pending.set(id, {
      resolve: resolve as (r: EvaluationResult | EvaluationResult[]) => void,
      fail: (error: string) =>
        resolve(expressions.map((expr) => ({ success: false as const, error, expression: expr }))),
      timer,
    });

    // Guard postMessage: structured-clone of `context` can throw (DataCloneError) — clean up
    // the pending entry/timer and resolve per-expression failures instead of rejecting.
    try {
      w.postMessage({ id, type: 'evaluateBatch', expressions, context } satisfies WorkerRequest);
    } catch (err) {
      clearTimeout(timer);
      pending.delete(id);
      resolve(
        expressions.map((expr) => ({
          success: false as const,
          error: `Failed to start evaluation: ${err instanceof Error ? err.message : String(err)}`,
          expression: expr,
        })),
      );
    }
  });
}

/** Terminate the worker (cleanup on unmount). Resolves any in-flight evaluations with a
 *  shape-correct failure so awaiting callers never hang. */
export function terminateWorker(): void {
  worker?.terminate();
  worker = null;
  for (const [, entry] of pending) {
    clearTimeout(entry.timer);
    entry.fail('Worker terminated');
  }
  pending.clear();
}
